import { LupaIcon } from "assets/icons/LupaIcon";
import { showErrorToast } from "components/CustomAlerts/CustomToast";
import { Input } from "components/Input/Input";
import { Pagination } from "components/Pagination/Pagination";
import { Select } from "components/Select/Select";
import { DEFAULT_LIMIT_PAGINATION } from "constants/appConstants";
import { useFormik } from "formik";
import { PlanEstudioI } from "interfaces/PlanEstudio";
import { useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PRIVATE_ROUTE } from "routes/privateRoutes";
import { getPlanesEstudio } from "services/planesEstudio";
import { fetchAlcances } from "store/slices/appSlice";
import { AppDispatch, RootState } from "store/store";
import { PlanesRowActions } from "./PlanesTable/PlanesRowActions";
import { PlanesTable } from "./PlanesTable/PlanesTable";

export const Planes = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [elementos, setElementos] = useState<PlanEstudioI[]>();
  const [cantidad, setCantidad] = useState<number>();
  const [page, setPage] = useState<number>();
  const [limit, setLimit] = useState<number>();

  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { alcances, loadingAppData } = useSelector(
    (st: RootState) => st.appData,
  );

  const initialValues = {
    nombre: "",
    alcance_id: "",
  };

  const { values, handleChange, resetForm } = useFormik({
    initialValues,
    onSubmit: () => {},
  });

  const searchPlanes = async (params?: {
    filtros?: { [key: string]: any };
  }) => {
    try {
      setIsLoading(true);
      const response = await getPlanesEstudio({ ...params?.filtros });
      setElementos(response.elementos);
      setCantidad(response.cantidad);
      setPage(response.page);
      setLimit(params?.filtros?.limit ?? DEFAULT_LIMIT_PAGINATION);
    } catch (err) {
      showErrorToast("No fue posible cargar los planes de estudio");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (elemento: PlanEstudioI) => {
    navigate(PRIVATE_ROUTE.PLANES_EDITAR + "/" + elemento.id);
  };

  const handleCreate = () => {
    navigate(PRIVATE_ROUTE.PLANES_CREAR);
  };

  const handleChangePagination = (newFiltros: {
    [key: string]: string | number;
  }) => {
    searchPlanes({ filtros: { ...values, ...newFiltros } });
  };

  const handleClearFilters = () => {
    resetForm();
    searchPlanes();
  };

  const handleChangeWithResetPage = (pageOne: any) => {
    setPage(1);
    handleChange(pageOne);
  };

  const refreshTable = () => {
    setPage(1);
    searchPlanes({ filtros: { ...values, page: 1 } });
  };

  const renderActions = (
    elemento: PlanEstudioI,
    onClickShowDetail: (elemento: PlanEstudioI) => void,
  ) => {
    return (
      <PlanesRowActions
        elemento={elemento}
        onClickEdit={handleEdit}
        refreshTable={refreshTable}
        onClickShowDetail={onClickShowDetail}
      />
    );
  };

  useEffect(() => {
    searchPlanes({ filtros: { ...values, limit, page } });
  }, [values]);

  useEffect(() => {
    if (!alcances) dispatch(fetchAlcances());
  }, []);

  return (
    <section>
      <h1 className="section-title">Planes de estudio</h1>

      <h2 className="section-subtitle mt-2">
        Desde acá podrás gestionar los planes de estudio.
      </h2>

      <div className="d-flex justify-content-between mt-4 mb-3">
        <div className="d-flex column-gap-3">
          <Input
            size="sm"
            id="nombre"
            onChange={handleChangeWithResetPage}
            placeholder="Buscar nombre"
            values={values}
            startIcon={<LupaIcon />}
          />
          <Select
            id="alcance_id"
            values={values}
            onChange={handleChangeWithResetPage}
            optionsFormatConfig={{ value: "id", label: "nombre" }}
            options={alcances ?? []}
            isLoading={loadingAppData.alcances}
            placeholder="Alcance"
            width="145px"
          />
          <Button
            size="sm"
            variant="secondary"
            className="clear-filters-button"
            onClick={handleClearFilters}
            disabled={!values.nombre && !values.alcance_id}
          >
            Limpiar filtros
          </Button>
        </div>
        <Button size="lg" onClick={handleCreate}>
          Nuevo Plan de estudio
        </Button>
      </div>

      {isLoading && (
        <div className="d-flex justify-content-center w-full">
          <Spinner />
        </div>
      )}

      {!isLoading && elementos && cantidad != undefined && (
        <>
          <PlanesTable elementos={elementos} renderActions={renderActions} />
          <Pagination
            totalItems={cantidad}
            currentPage={page}
            currentLimit={limit}
            onChangePage={handleChangePagination}
            showPaginationOnlyOnMoreThanPages={1}
          />
        </>
      )}
    </section>
  );
};
